import React, { useContext, useEffect, useState } from "react";
import AppError from "@/AppError";
import Dialog from "@/components/Dialog";
import Dropzone from "@/components/Form/Dropzone";
import {
  BMW_CONTRACT,
  ERROR,
  ScanStatus_Clean,
  ScanStatus_Malicious,
  SUCCESS,
  userNameKey,
  userQnumberKey,
} from "@/constants/constants";
import { AppContext } from "@/context/AppContext";
import utils from "@/utils/common";
import apiContractsData from "@/services/apiContracts";
import { useDocumentScanLinkEntityById} from "@/services/queries";
import { handleApiError } from "@/utils/handleApiError";
import { StyledField, StyledTextarea } from "@/components/Styles/PagesComponentsCommon";
import { useTranslation } from "react-i18next";
import apiUIData from "@/services/apiUI";

const TimelineDialog = ({
  id,
  open,
  setOpen,
  setIsSaving,
  setShowAlert,
  setUpdateTimeline,
}) => {
 
  const {
    setAlertStatus,
    setAlertMessage,
    userData: { [userQnumberKey]: userQNumber },
  } = useContext(AppContext);
  const { t } = useTranslation();
  const [base64File, setBase64File] = useState(null);
  const [comment, setComment] = useState("");
  const [errorTitle, setErrorTitle] = useState(false);
  const [files, setFiles] = useState([]);
  const [title, setTitle] = useState("");
  const [apiError, setApiError] = useState({});
  const [maxAttemptsReached, setMaxAttemptsReached] = useState(false);
  
  const [enableScanPolling, setEnableScanPolling] = useState(false);
  const [docScanLinkEntityId, setDocScanLinkEntityId] = useState(null);
  const [timeoutLimit, setTimeoutLimit] = useState(null);
  
  const acceptedFiles = ["application/pdf"];
  const errorMessage = t("timelineDialog.errorMessage");
  const filesLimit = 1;

  const scanQuery = useDocumentScanLinkEntityById(
    docScanLinkEntityId,
    {
      enabled: enableScanPolling,
      refetchIntervalMs: timeoutLimit,
      maxAttempts: 5,
    }
  );

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();

    apiUIData
      .getCrmConfiguration("NKAM.DealerPortal.Note.Timeout", controller)
      .then((response) => {
        const timeoutLimit = response?.data?.result?.integerValue ?? 5000;
        setTimeoutLimit(timeoutLimit);
      })
      .catch((error) => {        
        setApiError(handleApiError(error));                
        setTimeoutLimit(5000);
      });

    return () => controller.abort();
  }, [open]);

  useEffect(() => {
    if (title) {
      setErrorTitle(false);
    }
  }, [title]);

  useEffect(() => {
  if (!enableScanPolling) return;

  const timeoutId = setTimeout(() => {
    setEnableScanPolling(false);
    setIsSaving(false);
    setApiError({});
    setMaxAttemptsReached(true);
    handleClose();
    setShowAlert(true);
    setAlertStatus(ERROR);
    setAlertMessage(t("timelineDialog.errorTimeoutMessage"));

  }, 5 * timeoutLimit);

  return () => clearTimeout(timeoutId);
}, [enableScanPolling]);

  
useEffect(() => {
  if (!scanQuery.data) return;

  const isScanCompleted = scanQuery.data?.data?.result?.isScanCompleted;

  if (isScanCompleted && !maxAttemptsReached) {

    setEnableScanPolling(false);

    const scanResultValue = scanQuery.data?.data?.result?.scanResultValue;

    if (scanResultValue === ScanStatus_Clean) {
    
        apiContractsData
        .createContractTimelineNote({
          title,
          attachmentOrComments: t("timelineDialog.appendedComment", {
            comment,
            userName: utils.secureGetFromLocalStorage(userNameKey),
            cNumber: userQNumber,
          }),
          documentBody: base64File
            ? base64File.substr(base64File.indexOf(",") + 1)
            : null,
          fileName: files.length > 0 ? files[0]?.name : null,
          mimeType: files.length > 0 ? files[0]?.type : null,
          regarding: {
            id,
            logicalName: BMW_CONTRACT,
          },
        })
        .then(() => {
          setIsSaving(false);
          setApiError({});
          handleClose();
          setShowAlert(true);
          setAlertStatus(SUCCESS);
          setAlertMessage(t("timelineDialog.alertMessage"));
          setUpdateTimeline(true);
        })
        .catch((err) => {
          setIsSaving(false);
          setApiError(handleApiError(err));
        });  
    } 
    else if (scanResultValue === ScanStatus_Malicious) {
    
      const uploadedFileName = files.length > 0 ? files[0]?.name : null;

      apiContractsData
      .createContractTimelineNote({
        title : t("timelineDialog.maliciousFileSubject"),
        attachmentOrComments: t("timelineDialog.appendedComment", {
          comment : t("timelineDialog.maliciousFileComment") + uploadedFileName,
          userName: utils.secureGetFromLocalStorage(userNameKey),
          cNumber: userQNumber,
        }),
        documentBody: null,
        fileName: null,
        mimeType: null,
        regarding: {
          id,
          logicalName: BMW_CONTRACT,
        },
      })
      .then(() => {
        setIsSaving(false);
        setApiError({});
        handleClose();
        setShowAlert(true);
        setAlertStatus(ERROR);
        setAlertMessage(t("timelineDialog.errorMaliciousFileMessage"));  
        setUpdateTimeline(true);
      })
      .catch((err) => {
        setIsSaving(false);
        setApiError(handleApiError(err));
      });  
    }
    else {
        setIsSaving(false);
        setApiError(handleApiError({ message: t("timelineDialog.errorInvalidScanResult")}));
    }
  }
}, [scanQuery.data]);

  const clearForm = () => {
    setBase64File(null);
    setComment("");
    setErrorTitle(false);
    setFiles([]);
    setTitle("");
  };

  const handleClose = () => {
    setOpen(false);
    clearForm();
  };

  const validateForm = () => {
    if (title) {
      setErrorTitle(false);
    } else {
      setErrorTitle(true);
    }
  };

  const createContractTimelineNote = async () => {
    validateForm();

    // Append username and C number to end of comment
    const appendedComment =t("timelineDialog.appendedComment", { comment: comment, userName: utils.secureGetFromLocalStorage(userNameKey), cNumber: userQNumber })

    if (title) {
      setIsSaving(true);

      if(files.length > 0) {

        const createDocScanLinkData = {
          callingEntityName: BMW_CONTRACT,
          callingEntityId: id,
          fileName: files.length > 0 ? files[0]?.name : null,
          mimeType: files.length > 0 ? files[0]?.type : null,
        };

        await apiContractsData.createDocScanLink(createDocScanLinkData)
        .then((responseCreateDocScanLink) => {
            
            if (responseCreateDocScanLink?.statusCode === 200 && responseCreateDocScanLink?.data?.result)
            {
                setDocScanLinkEntityId(responseCreateDocScanLink.data.result);

                const uploadData = {
                    entityId: id,
                    docScanLinkEntityId: responseCreateDocScanLink.data.result,
                    fileName: files.length > 0 ? files[0].name : null,
                    mimeType: files.length > 0 ? files[0].type : null,
                    base64File: base64File
                      ? base64File?.substr(base64File.indexOf(",") + 1)
                      : null,
                  };

                  apiContractsData.uploadDocForScanning(uploadData)
                  .then((responseUpload) => {
                    
                    if(responseUpload?.statusCode === 200)
                    { 
                      setEnableScanPolling(true);
                    }
                  })
                  .catch((error) => {
                    setApiError(handleApiError(error));
                  });
            }
            else 
            {
              setApiError(handleApiError({ message: "Scan link creation failed or GUID missing for Contract."}));
            }
          })
          .catch((error) => {
            setApiError(handleApiError(error));
          });
      }
      else{
        
          const data = {
          title: title,
          attachmentOrComments: appendedComment,
          documentBody: null,
          fileName: null,
          mimeType: null,
          regarding: {
            id: id,
            logicalName: BMW_CONTRACT,
          },
        };
        
        await apiContractsData
          .createContractTimelineNote(data)
          .then(() => {
            setIsSaving(false);
            setApiError({});
            handleClose();
            setShowAlert(true);
            setAlertStatus(SUCCESS);
            setAlertMessage(t("timelineDialog.alertMessage"));
            setUpdateTimeline(true);
            })
          .catch((error) => {
            setIsSaving(false);
            setApiError(handleApiError(error));
          });  
      }
    }
  };

  return (
    <>
      <Dialog
        confirmationButtonText={t("formActions.create")}
        handleClose={handleClose}
        handler={createContractTimelineNote}
        id="createContractNote"
        open={open}
        prompt={t("timelineDialog.prompt")}
        title={t("timelineDialog.contractTitle")}
      >
        <StyledField
          label={t("timelineDialog.dialogLabel")}
          error={!!errorTitle}
          required
          value={title}
          helperText={errorTitle ? errorMessage : null}
          onChange={(event) => {
            setTitle(event.target.value);
          }}
        />

        <StyledTextarea
          label={t("timelineDialog.textAreaLabel")}
          value={comment}
          onChange={(event) => {
            setComment(event.target.value);
          }}
          multiline
          rows={6}
        />

        <Dropzone
          acceptedFiles={acceptedFiles}
          files={files}
          filesLimit={filesLimit}
          setBase64File={setBase64File}
          setFiles={setFiles}
        />
      </Dialog>

      {apiError?.isError && (
        <AppError apiError={apiError} setApiError={setApiError} />
      )}
    </>
  );
};

export default TimelineDialog;
